/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import {
	GridApi,
	GridOptions,
	GridReadyEvent,
	SortChangedEvent,
	SortDirection
} from "ag-grid-community";
import { EChartsOption } from "echarts";
import { finalize, Subject, takeUntil, tap } from "rxjs";
import { ButtonComponent } from "../../shared/components/button/button.component";
import { ChartComponent } from "../../shared/components/chart/chart.component";
import { DropdownComponent } from "../../shared/components/dropdown/dropdown.component";
import { PopoverFloatingComponent } from "../../shared/components/popover/popover.component";
import { PaginationComponent } from "../../shared/components/table/pagination/pagination.component";
import { TableComponent } from "../../shared/components/table/table.component";
import { AnomalyDetails } from "../../shared/models/http/anomaly/anomaly-details.model";
import { LineChart } from "../../shared/models/http/charts/line-chart/line-chart.model";
import { FilterRequest } from "../../shared/models/http/dashboard/filter-page-response.model";
import { ChartResponse } from "../../shared/models/ui/charts.model";
import { SelectOption } from "../../shared/models/ui/select-option.model";
import { AnomalyDetectionService } from "../../shared/services/anomaly-detection.service";
import { DashboardSoftwareAssetsService } from "../../shared/services/dashboard-software-assets.service";
import { StorageService } from "../../shared/services/storage.service";
import { COLORS, PATHS } from "../../shared/utils/constants";
import {
	formatNumberMaxDecimals,
	getTableIndex,
	prettifyISODate,
	roundValueUpTo
} from "../../shared/utils/utils";

@Component({
	selector: "globalictcc-anomalies-detection-details",
	standalone: true,
	imports: [
		ButtonComponent,
		PaginationComponent,
		TableComponent,
		PopoverFloatingComponent,
		DropdownComponent,
		ReactiveFormsModule,
		ChartComponent
	],
	templateUrl: "./anomalies-detection-details.component.html",
	styleUrl: "./anomalies-detection-details.component.scss"
})
export class AnomaliesDetectionDetailsComponent implements OnInit, OnDestroy {
	public filterFormGroup = new FormGroup({
		types: new FormControl<string[]>([], [Validators.required]),
		layers: new FormControl<string[]>([], [Validators.required])
	});

	public applicationName: string = "";
	public anomaliesDetailsTableHasError: boolean = false;
	public currentPage: number = 0;
	public pageSize: number = 12;
	public totalItems: number = 0;
	public totalPages: number = 1;
	public pageItems: number = 0;
	public gridOptions: GridOptions = {
		columnDefs: [
			{
				headerName: "No.",
				sortable: false,
				valueFormatter: params =>
					getTableIndex(this.currentPage, this.pageSize, params.node?.rowIndex)
			},
			{ headerName: "Type", field: "anomalyType" },
			{
				headerName: "Timestamp",
				field: "timestamp",
				valueFormatter: params => (params.value ? prettifyISODate(params.value) : "")
			},
			{ headerName: "Application", field: "applicationId" },
			{ headerName: "Layer", field: "layerId" },
			{
				headerName: "Score",
				field: "anomalyScore",
				valueFormatter: params =>
					params.value !== undefined
						? formatNumberMaxDecimals(roundValueUpTo(params.value))
						: ""
			}
		],
		rowData: [],
		onGridReady: this._onGridReady.bind(this),
		onSortChanged: this._onSortChanged.bind(this)
	};

	public typeOptions: SelectOption[] = [
		{ label: "SCI", value: "SCI" },
		{ label: "CO2", value: "CO2" },
		{ label: "E", value: "E" }
	];
	public layerOptions: SelectOption[] = [];
	public hasMoreLayers: boolean = false;
	public isLayersLoadingMore: boolean = false;

	public sciLineChart?: ChartResponse<LineChart>;
	public co2LineChart?: ChartResponse<LineChart>;
	public sciComponentELineChart?: ChartResponse<LineChart>;
	public isSciLineChartLoading: boolean = false;
	public isCo2LineChartLoading: boolean = false;
	public isSciComponentELineChartLoading: boolean = false;
	public co2Overrides: EChartsOption = {
		yAxis: {
			name: ""
		},
		series: {
			color: COLORS.YELLOW_100,
			showSymbol: false
		}
	};

	public sciOverrides: EChartsOption = {
		...this.co2Overrides
	};

	public sciComponentEOverrides: EChartsOption = {
		...this.co2Overrides
	};

	private _startDate: string;
	private _endDate: string;
	private _tenantId: string;
	private _gridApi: GridApi;
	private _destroy$ = new Subject<void>();

	private _layersCursor: string | null = null;
	private _sortField?: string;
	private _sortDirection?: SortDirection;

	constructor(
		private _router: Router,
		private _route: ActivatedRoute,
		private _anomalyDetectionService: AnomalyDetectionService,
		private _storageService: StorageService,
		private _dashboardSoftwareAssetsService: DashboardSoftwareAssetsService
	) {}

	ngOnInit() {
		this._tenantId = this._storageService.getTenantId();

		this._route.queryParams.pipe(takeUntil(this._destroy$)).subscribe({
			next: params => {
				const { applicationName, startDate, endDate } = params;

				if (applicationName && startDate) {
					this.applicationName = applicationName;
					this._startDate = startDate;
					this._endDate = endDate;

					this._layersCursor = null;
					this._fetchLayerIds("", null);
				} else {
					this.onGoBack();
				}
			}
		});

		this.filterFormGroup.patchValue({
			types: this.typeOptions.map(option => option.value)
		});
	}

	ngOnDestroy() {
		this._destroy$.next();
		this._destroy$.complete();
	}

	/**
	 * Navigates back to the previous page using the environment service.
	 * This method is typically called when the user wants to return to the previous view.
	 */
	public onGoBack() {
		this._router.navigate([PATHS.ANOMALIES_DETECTION]);
	}

	/**
	 * Applies the filter and fetches the application anomalies.
	 * This method is triggered when the filter is applied.
	 * It calls the private method `_fetchApplicationAnomalies` to retrieve the filtered anomalies.
	 */
	public onFilterApply() {
		this.fetchApplicationAnomalies();
	}

	/**
	 * Navigates to the previous page of application anomalies.
	 * Decrements the current page number and fetches the anomalies for the updated page.
	 */
	public onPreviousPage() {
		this.currentPage--;

		this.fetchApplicationAnomalies();
	}

	/**
	 * Advances to the next page of application anomalies.
	 * Increments the current page number and fetches the anomalies for the new page.
	 */
	public onNextPage() {
		this.currentPage++;

		this.fetchApplicationAnomalies();
	}

	/**
	 * Event handler for the grid's 'gridReady' event.
	 * This method is called when the grid has finished initializing.
	 * It resizes the columns to fit the grid's width.
	 *
	 * @param params - The event parameters containing the grid API.
	 */
	private _onGridReady(params: GridReadyEvent) {
		this._gridApi = params.api;

		this.fetchApplicationAnomalies();
	}

	/**
	 * Handles the sort changed event for the grid.
	 *
	 * This method is triggered when the sorting of columns in the grid changes.
	 * It identifies the column that has been sorted and updates the internal
	 * sort field and direction accordingly. After updating the sort parameters,
	 * it fetches the application anomalies based on the new sort order.
	 *
	 * @param params - The event parameters containing the sort state of the grid.
	 */
	private _onSortChanged(params: SortChangedEvent) {
		const columnWithSort = params.api.getColumnState()?.find(col => col.sort !== null);
		this._sortField = columnWithSort?.colId;
		this._sortDirection = columnWithSort?.sort;

		this.fetchApplicationAnomalies();
	}

	/** Called by the Layer dropdown when scroll reaches the bottom. */
	public onLayerLoadMore(): void {
		if (!this.hasMoreLayers || this.isLayersLoadingMore) return;
		this._fetchLayerIds("", this._layersCursor, true);
	}

	private _fetchLayerIds(
		searchTerm: string,
		cursor: string | null,
		isLoadingMore: boolean = false
	): void {
		if (!isLoadingMore) {
			this.layerOptions = [];
		}
		this.isLayersLoadingMore = true;

		const body: FilterRequest = {
			allSelected: false,
			selectedIds: [this.applicationName]
		};

		this._dashboardSoftwareAssetsService
			.getLayerIds(body, searchTerm, cursor, 20)
			.pipe(
				takeUntil(this._destroy$),
				finalize(() => (this.isLayersLoadingMore = false))
			)
			.subscribe(response => {
				const mapped = response.content.map(layer => ({ label: layer, value: layer }));
				this.layerOptions = isLoadingMore ? [...this.layerOptions, ...mapped] : mapped;
				this.hasMoreLayers = response.hasNext;
				this._layersCursor = response.nextCursor ?? null;

				if (!isLoadingMore) {
					this.filterFormGroup.patchValue({
						layers: this.layerOptions.map(option => option.value)
					});
				}
			});
	}

	/**
	 * Fetches application anomalies based on the current filter and pagination settings.
	 *
	 * This method retrieves anomalies for a specific application using the provided filter criteria
	 * such as type, layer, date range, and pagination settings. The anomalies are then processed
	 * and the page data is updated accordingly.
	 */
	public fetchApplicationAnomalies() {
		this.isSciLineChartLoading = true;
		this.isCo2LineChartLoading = true;
		this.isSciComponentELineChartLoading = true;

		this._gridApi.setGridOption("loading", true);

		this._anomalyDetectionService
			.getAnomaliesByApplication(
				this.applicationName,
				this._startDate,
				this._tenantId,
				this.currentPage,
				this.pageSize,
				this._endDate,
				this.filterFormGroup.controls.layers.value || undefined,
				this.filterFormGroup.controls.types.value || undefined,
				this._sortField,
				undefined, // TODO: Check if it's needed
				this._sortDirection
			)
			.pipe(
				tap({
					next: anomalyDetails => {
						this._updatePageData(anomalyDetails);
					},
					error: () => {
						this.anomaliesDetailsTableHasError = true;
						this.sciLineChart = { chart: {} as LineChart, hasError: true };
						this.co2LineChart = { chart: {} as LineChart, hasError: true };
						this.sciComponentELineChart = { chart: {} as LineChart, hasError: true };
					},
					finalize: () => {
						this.isSciLineChartLoading = false;
						this.isCo2LineChartLoading = false;
						this.isSciComponentELineChartLoading = false;

						this._gridApi.setGridOption("loading", false);
					}
				})
			)
			.subscribe();
	}

	/**
	 * Updates the page data with the provided application anomalies.
	 *
	 * @param {AnomalyDetails} anomalyDetails - The details of the application anomalies.
	 *
	 * This method performs the following actions:
	 * - Sets the grid option "rowData" with the anomalies data, including an id generated by `getTableIndex`.
	 * - Updates the `sciLineChart`, `co2LineChart`, and `sciComponentELineChart` properties with the corresponding data from `applicationAnomalies`.
	 * - Updates pagination properties such as `totalPages`, `totalItems` and `pageItems` based on the `pagedTable` data.
	 */
	private _updatePageData(anomalyDetails: AnomalyDetails) {
		const {
			pagedTable: page,
			sciLineChart,
			co2LineChart,
			sciComponentELineChart
		} = anomalyDetails;

		this.anomaliesDetailsTableHasError = false;

		this.pageItems = page.contents.length;
		this.totalPages = page.totalPages;
		this.totalItems = page.totalElements;
		this.pageSize = page.size;

		this.sciLineChart = { chart: sciLineChart, hasError: false };
		this.co2LineChart = { chart: co2LineChart, hasError: false };
		this.sciComponentELineChart = { chart: sciComponentELineChart, hasError: false };

		this._gridApi.setGridOption("rowData", page.contents);
	}
}
